import React, { useState, useEffect, useCallback, useId, useRef } from "react";
import {
  MdFilterList,
  MdPeople,
  MdRefresh,
  MdExpandMore,
  MdExpandLess,
  MdClose,
  MdCheck,
} from "react-icons/md";
import AtriumApi from "api";
import Badge from "components/badge/Badge";
import Button from "components/button";
import Loader from "components/loader/Loader";
import { FormField, InputField, Select, getControlClassName } from "components/fields";
import OptionGroup from "components/heartbeat/OptionGroup";
import { EMPTY, formatNumber } from "utils/format";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * MultiSelectDropdown - Reusable multi-select dropdown component
 */
const MultiSelectDropdown = ({
  label,
  options,
  selectedIds,
  onChange,
  placeholder = "Select...",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    return () => document.removeEventListener("mousedown", handlePointer);
  }, [isOpen]);

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  const toggleOption = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((i) => i !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const selectedCount = selectedIds.length;
  const selectedLabels = options
    .filter((o) => selectedIds.includes(o.id))
    .map((o) => o.name)
    .slice(0, 2);
  const ExpandIcon = isOpen ? MdExpandLess : MdExpandMore;

  return (
    <div ref={rootRef} className="relative" onKeyDown={handleKeyDown}>
      <FormField label={label}>
        {(controlProps) => (
          <button
            ref={triggerRef}
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            aria-expanded={isOpen}
            aria-controls={isOpen ? listId : undefined}
            {...controlProps}
            className={getControlClassName({
              className: "flex items-center justify-between gap-2 text-left",
            })}
          >
            <span className={cx("truncate", selectedCount > 0 ? "text-fg" : "text-fg-muted")}>
              {selectedCount > 0
                ? selectedLabels.join(", ") + (selectedCount > 2 ? ` +${selectedCount - 2}` : "")
                : placeholder}
            </span>
            <ExpandIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-fg-muted" />
          </button>
        )}
      </FormField>

      {isOpen && (
        <div
          id={listId}
          role="group"
          aria-label={label}
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-control border border-line bg-surface py-1 shadow-overlay"
        >
          {options.length === 0 ? (
            <p className="px-4 py-3 text-sm text-fg-muted">No options available</p>
          ) : (
            options.map((option) => {
              const selected = selectedIds.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => toggleOption(option.id)}
                  className={cx(
                    "flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm text-fg transition-colors focus:outline-none focus-visible:bg-surface-subtle",
                    selected ? "bg-brand-50 dark:bg-brand-900/20" : "hover:bg-surface-subtle"
                  )}
                >
                  <span>{option.name}</span>
                  {selected && (
                    <MdCheck aria-hidden="true" className="h-4 w-4 shrink-0 text-primary dark:text-accent" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

/**
 * RoleCheckboxGroup - Toggle buttons for selecting roles
 */
const RoleCheckboxGroup = ({ selectedRoles, onChange, roles }) => {
  const toggleRole = (value) => {
    if (selectedRoles.includes(value)) {
      onChange(selectedRoles.filter((r) => r !== value));
    } else {
      onChange([...selectedRoles, value]);
    }
  };

  return (
    <OptionGroup label="Roles">
      <div className="flex flex-wrap gap-2">
        {roles.map((role) => {
          const selected = selectedRoles.includes(role.value);
          return (
            <Button
              key={role.value}
              size="sm"
              variant={selected ? "primary" : "secondary"}
              aria-pressed={selected}
              onClick={() => toggleRole(role.value)}
            >
              {role.label}
            </Button>
          );
        })}
      </div>
    </OptionGroup>
  );
};

/**
 * MembershipAgeInput - Input for membership age filter
 */
const MembershipAgeInput = ({ days, operator, onDaysChange, onOperatorChange }) => {
  return (
    <OptionGroup label="Membership Age">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Membership age condition"
          className="w-48"
          value={operator}
          onChange={(e) => onOperatorChange(e.target.value)}
          options={[
            { value: "lte", label: "Joined within" },
            { value: "gte", label: "Joined more than" },
          ]}
        />
        <InputField
          aria-label="Membership age in days"
          className="w-24"
          type="number"
          min="0"
          value={days || ""}
          onChange={(e) => onDaysChange(e.target.value ? parseInt(e.target.value) : null)}
          placeholder="30"
        />
        <span className="text-sm text-fg-muted">days ago</span>
      </div>
    </OptionGroup>
  );
};

/**
 * RecipientFilterBuilder - Main filter builder component
 */
const RecipientFilterBuilder = ({
  organizationId,
  initialFilters = {},
  onFilterChange,
  onRecipientCountChange,
  showPreview = true,
}) => {
  const [filterOptions, setFilterOptions] = useState({
    campuses: [],
    groups: [],
    tags: [],
    roles: [],
    surveyStatuses: [],
  });
  const [filters, setFilters] = useState({
    campusIds: [],
    groupIds: [],
    tagIds: [],
    roles: [],
    surveyStatus: "any",
    membershipAgeDays: null,
    membershipAgeOperator: "lte",
    ...initialFilters,
  });
  const [recipientCount, setRecipientCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const panelId = useId();

  // Fetch filter options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const response = await AtriumApi.get("/v2/heartbeat/campaigns/filter-options");
        if (response.data.success) {
          setFilterOptions(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  // Fetch recipient count when filters change
  const fetchRecipientCount = useCallback(async () => {
    try {
      setLoadingCount(true);
      const filterCriteria = buildFilterCriteria(filters);
      const response = await AtriumApi.post("/v2/heartbeat/campaigns/filter-count", {
        filterCriteria,
      });
      if (response.data.success) {
        const count = response.data.data.count;
        setRecipientCount(count);
        onRecipientCountChange?.(count);
      }
    } catch (error) {
      console.error("Error fetching recipient count:", error);
    } finally {
      setLoadingCount(false);
    }
  }, [filters, onRecipientCountChange]);

  // Debounced fetch count
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecipientCount();
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, fetchRecipientCount]);

  // Build filter criteria object for API
  const buildFilterCriteria = (f) => {
    const criteria = {};
    if (f.campusIds?.length > 0) criteria.campusIds = f.campusIds;
    if (f.groupIds?.length > 0) criteria.groupIds = f.groupIds;
    if (f.tagIds?.length > 0) criteria.tagIds = f.tagIds;
    if (f.roles?.length > 0) criteria.roles = f.roles;
    if (f.surveyStatus && f.surveyStatus !== "any") criteria.surveyStatus = f.surveyStatus;
    if (f.membershipAgeDays) {
      criteria.membershipAgeDays = f.membershipAgeDays;
      criteria.membershipAgeOperator = f.membershipAgeOperator;
    }
    return Object.keys(criteria).length > 0 ? criteria : null;
  };

  // Update filters and notify parent
  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange?.(buildFilterCriteria(newFilters));
  };

  // Clear all filters
  const clearFilters = () => {
    const clearedFilters = {
      campusIds: [],
      groupIds: [],
      tagIds: [],
      roles: [],
      surveyStatus: "any",
      membershipAgeDays: null,
      membershipAgeOperator: "lte",
    };
    setFilters(clearedFilters);
    onFilterChange?.(null);
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.campusIds?.length > 0 ||
    filters.groupIds?.length > 0 ||
    filters.tagIds?.length > 0 ||
    filters.roles?.length > 0 ||
    (filters.surveyStatus && filters.surveyStatus !== "any") ||
    filters.membershipAgeDays;

  return (
    <div className="rounded-control border border-line">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full flex-wrap items-center justify-between gap-2 rounded-control px-4 py-3 text-left focus-ring"
      >
        <span className="flex items-center gap-2">
          <MdFilterList aria-hidden="true" className="h-5 w-5 text-primary dark:text-accent" />
          <span className="font-semibold text-fg">Recipient Filters</span>
          {hasActiveFilters && <Badge tone="brand">Active</Badge>}
        </span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-2 text-sm font-medium text-fg-muted">
            <MdPeople aria-hidden="true" className="h-4 w-4" />
            {loadingCount ? (
              <Loader size="xs" variant="inline" label="Counting recipients" />
            ) : recipientCount !== null ? (
              `${formatNumber(recipientCount)} recipients`
            ) : (
              EMPTY
            )}
          </span>
          {isExpanded ? (
            <MdExpandLess aria-hidden="true" className="h-5 w-5 text-fg-muted" />
          ) : (
            <MdExpandMore aria-hidden="true" className="h-5 w-5 text-fg-muted" />
          )}
        </span>
      </button>

      {isExpanded && (
        <div id={panelId} className="border-t border-line px-4 py-4">
          {loading ? (
            <Loader label="Loading filters" />
          ) : (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <MultiSelectDropdown
                  label="Campus"
                  options={filterOptions.campuses}
                  selectedIds={filters.campusIds}
                  onChange={(ids) => updateFilter("campusIds", ids)}
                  placeholder="All campuses"
                />
                <MultiSelectDropdown
                  label="Groups"
                  options={filterOptions.groups}
                  selectedIds={filters.groupIds}
                  onChange={(ids) => updateFilter("groupIds", ids)}
                  placeholder="All groups"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <MultiSelectDropdown
                  label="Tags"
                  options={filterOptions.tags}
                  selectedIds={filters.tagIds}
                  onChange={(ids) => updateFilter("tagIds", ids)}
                  placeholder="Any tag"
                />
                <RoleCheckboxGroup
                  selectedRoles={filters.roles}
                  onChange={(roles) => updateFilter("roles", roles)}
                  roles={filterOptions.roles}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <Select
                  label="Survey Status"
                  value={filters.surveyStatus}
                  onChange={(e) => updateFilter("surveyStatus", e.target.value)}
                  options={filterOptions.surveyStatuses}
                />
                <MembershipAgeInput
                  days={filters.membershipAgeDays}
                  operator={filters.membershipAgeOperator}
                  onDaysChange={(days) => updateFilter("membershipAgeDays", days)}
                  onOperatorChange={(op) => updateFilter("membershipAgeOperator", op)}
                />
              </div>

              <div className="flex items-center justify-between border-t border-line pt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<MdClose aria-hidden="true" className="h-4 w-4" />}
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<MdRefresh aria-hidden="true" className="h-4 w-4" />}
                  loading={loadingCount}
                  onClick={fetchRecipientCount}
                >
                  Refresh Count
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientFilterBuilder;
